import Droppable from "@/components/molecules/Droppable/Droppable";
import { SquadBuilderPlayer } from "@/components/molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { thickArrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { findMetaRating } from "@/redux/squadBuilder/findMetaRating";
import { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { useDraggable } from "@dnd-kit/core";
import { useTranslation } from "@repo/shared/i18n";
import { MetaRating } from "@repo/shared/types";
import { captureMessage } from "@sentry/react";

import { PitchItem } from "../PitchItem/PitchItem";
import { PitchRoleTile } from "../PitchRoleTile/PitchRoleTile";

type BenchProps = {
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  handleAddPlayer: (index: number) => void;
  handleRemovePlayer: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  handleOnClickPlayer: (index: number) => void;
  handleOnClickPlayerMobile: (index: number) => void;
  changingPitchItemOnIndex: number;
  rightPanelMode: string;
  tabletOrLargerMode: boolean;
  isOverItem?: number;
  desktopMode: boolean;
  tacticsView?: boolean;
};

const DraggableItem = ({
  id,
  className,
  children,
}: {
  id: number;
  className?: string;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        // Hover lifts the card so its menu paints over the next one
        "relative hover:z-30 text-center touch-none",
        className,
        isDragging && "opacity-50",
      )}
    >
      {children}
    </div>
  );
};

const BenchItem = ({
  item,
  benchIndex,
  isOverItem,
  handleAddPlayer,
  handleRemovePlayer,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  handleChangePlayerRole,
  changingPitchItemOnIndex,
  rightPanelMode,
  tabletOrLargerMode,
  desktopMode,
  tacticsView,
}: {
  item: SquadBuilderPlayerType;
  benchIndex: number;
  isOverItem?: number;
  handleAddPlayer: (index: number) => void;
  handleRemovePlayer: (index: number) => void;
  handleChangePlayerRole: (index: number) => void;
  handleOnClickPlayer: (index: number) => void;
  handleOnClickPlayerMobile: (index: number) => void;
  changingPitchItemOnIndex: number;
  rightPanelMode: string;
  tabletOrLargerMode: boolean;
  desktopMode: boolean;
  tacticsView?: boolean;
}) => {
  const isPlayer = item.resourceId;
  let metaRatingObject: MetaRating;

  if (isPlayer) {
    try {
      metaRatingObject = findMetaRating({
        metaRatings: item.metaRatings,
        playerRoleId: item.playerRoleId,
        chemistry: 0,
      });
    } catch (error) {
      metaRatingObject = { metaRating: 0, chemstyleId: 0 };
      captureMessage(error instanceof Error ? error.message : String(error), {
        level: "error",
        tags: {
          component: "Bench Meta Rating Object",
        },
      });
    }
  }

  // Tactics view swaps the slot for the pitch's role tile
  const roleTile = tacticsView && (
    <PitchRoleTile
      name={item.cardName}
      playerRoleId={item.playerRoleId}
      rolesPlus={item.rolesPlus}
      position={item.position}
      isActive={
        changingPitchItemOnIndex === benchIndex || isOverItem === benchIndex
      }
      onClick={() => handleChangePlayerRole(benchIndex)}
    />
  );

  return (
    <div
      // Hover lifts an empty slot's menu over the next slot
      className="relative hover:z-30 h-[130px] w-[70px] desktop:min-w-[112px] desktop:h-[195px] sb-v2-lg:h-[180px]  desktop:mb-6 sb-v2-lg:mb-4 flex justify-center items-center desktop:mt-auto"
      key={benchIndex}
    >
      <Droppable id={benchIndex}>
        <div className="touch-device:-mx-4">
          {isPlayer ? (
            <DraggableItem
              id={benchIndex}
              className={
                roleTile
                  ? undefined
                  : "scale-[90%] sb-v2-lg:scale-[85%] h-[130px] desktop:-mt-8 sb-v2-lg:-mt-12 -mx-4"
              }
            >
              {roleTile || (
                <SquadBuilderPlayer
                  player={item}
                  isOnPitch={!desktopMode}
                  isOnBench={true}
                  position={
                    item.possiblePositions?.includes(item.position)
                      ? item.position
                      : item.preferredPosition
                  }
                  formationPosition={item.position}
                  metaRating={metaRatingObject?.metaRating}
                  metaRatingZeroChem={metaRatingObject?.metaRating}
                  chemStyleId={0}
                  playerRoleId={item.playerRoleId}
                  tabletOrLargerMode={tabletOrLargerMode}
                  desktopMode={desktopMode}
                  handleRemovePlayer={() => handleRemovePlayer(benchIndex)}
                  isSelected={changingPitchItemOnIndex === benchIndex}
                  onClickPlayerRole={() => handleChangePlayerRole(benchIndex)}
                  onClickPlayer={(e) => {
                    if (desktopMode) {
                      handleOnClickPlayer(benchIndex);
                    } else {
                      handleOnClickPlayerMobile(benchIndex);
                      e.stopPropagation();
                    }
                  }}
                />
              )}
            </DraggableItem>
          ) : (
            roleTile || (
              <div
                className={cn(
                  // Undimmed on hover so the menu isn't dimmed with it
                  "w-full relative flex flex-col items-center hover:brightness-100",
                  isOverItem === benchIndex ||
                    benchIndex === changingPitchItemOnIndex
                    ? "brightness-100"
                    : "brightness-75",
                )}
              >
                <PitchItem
                  onClickPlayerPlaceHolder={() => handleAddPlayer(benchIndex)}
                  onClickRole={() => handleChangePlayerRole(benchIndex)}
                  activePlayerPlaceHolder={
                    changingPitchItemOnIndex === benchIndex &&
                    (rightPanelMode === "add-player" ||
                      rightPanelMode === "add-player-role")
                  }
                  isOnBench={true}
                  disablePlayerFilters={true}
                  numberOfFilters={0}
                  tabletOrLargerMode={tabletOrLargerMode}
                  // Same spot as the pitch, tucked under the placeholder
                  formationPosition={item.position}
                />
              </div>
            )
          )}
        </div>
      </Droppable>
    </div>
  );
};
const Bench = ({
  benchItems,
  tempBenchItems,
  handleAddPlayer,
  handleRemovePlayer,
  handleChangePlayerRole,
  handleOnClickPlayer,
  handleOnClickPlayerMobile,
  changingPitchItemOnIndex,
  rightPanelMode,
  tabletOrLargerMode,
  desktopMode,
  isOverItem,
  tacticsView,
}: BenchProps) => {
  const { t } = useTranslation("squadBuilder");
  const substitutes =
    tempBenchItems.length > 0
      ? tempBenchItems.slice(0, 7)
      : benchItems.slice(0, 7);
  const reserves =
    tempBenchItems.length > 0 ? tempBenchItems.slice(7) : benchItems.slice(7);

  return (
    <>
      <button
        className="rotate-180 p-2 hidden desktop:block"
        onClick={() => {
          const scrollArea = document.querySelector(
            "[data-radix-scroll-area-viewport]",
          );
          if (scrollArea) {
            scrollArea.scrollTo({
              left: 0,
              behavior: "smooth",
            });
          }
        }}
      >
        {thickArrow}
      </button>
      <ScrollArea className="mb-6">
        <div className="flex items-center flex-col">
          <div className="flex flex-row gap-x-8">
            <div className="flex flex-col">
              <div
                className="w-full pl-2 text-xs py-0.5 rounded-t-md"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                }}
              >
                {t("pitch.subs")}
              </div>
              <div className="flex flex-row gap-x-4">
                {substitutes.map((item, index) => {
                  return (
                    <BenchItem
                      key={index}
                      item={item}
                      benchIndex={index + 11}
                      isOverItem={isOverItem}
                      handleAddPlayer={handleAddPlayer}
                      handleRemovePlayer={handleRemovePlayer}
                      handleChangePlayerRole={handleChangePlayerRole}
                      handleOnClickPlayer={handleOnClickPlayer}
                      handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                      changingPitchItemOnIndex={changingPitchItemOnIndex}
                      rightPanelMode={rightPanelMode}
                      tabletOrLargerMode={tabletOrLargerMode}
                      desktopMode={desktopMode}
                      tacticsView={tacticsView}
                    />
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col">
              <div
                className="w-full pl-2 text-xs py-0.5 rounded-t-md"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)",
                }}
              >
                {t("pitch.reserves")}
              </div>
              <div className="flex flex-row gap-x-4">
                {reserves.map((item, index) => {
                  return (
                    <BenchItem
                      key={index}
                      item={item}
                      benchIndex={index + 18}
                      isOverItem={isOverItem}
                      handleAddPlayer={handleAddPlayer}
                      handleRemovePlayer={handleRemovePlayer}
                      handleChangePlayerRole={handleChangePlayerRole}
                      handleOnClickPlayer={handleOnClickPlayer}
                      handleOnClickPlayerMobile={handleOnClickPlayerMobile}
                      changingPitchItemOnIndex={changingPitchItemOnIndex}
                      rightPanelMode={rightPanelMode}
                      tabletOrLargerMode={tabletOrLargerMode}
                      desktopMode={desktopMode}
                      tacticsView={tacticsView}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <ScrollBar
          orientation="horizontal"
          className="overflow-y-hidden cursor-grab"
          forceMount
        />
      </ScrollArea>
      <button
        className="p-2 hidden desktop:block"
        onClick={() => {
          const scrollArea = document.querySelector(
            "[data-radix-scroll-area-viewport]",
          );
          if (scrollArea) {
            scrollArea.scrollTo({
              left: scrollArea.scrollWidth,
              behavior: "smooth",
            });
          }
        }}
      >
        {thickArrow}
      </button>
    </>
  );
};

export default Bench;
